import { Component, OnInit, OnChanges, OnDestroy, Input } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { RepairDiagramDialogComponent } from 'app/modules/app-components/repair-diagram-dialog/repair-diagram-dialog.component';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';

import { CalculationService, DataService, HelperService, HTTPService, SocketService } from 'app/services';
import { units } from 'app/constants';
import { currency } from 'app/constants/currency.constants';

import { sortBy } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-motor-service-enhanced',
  templateUrl: './motor-service-enhanced.component.html',
  styleUrls: ['./motor-service-enhanced.component.scss'],
})
export class MotorServiceEnhancedComponent implements OnInit {
  @Input() orderStatus: string = '';
  @Input() selectedMotorServices: any = {};
  @Input() motorVehicle: any = {};
  @Input() motorVehicleId: string = '';
  @Input() query: string = '';
  @Input() isMotorServiceEnabled: boolean = false;
  @Input() isRepairDiagramEnabled: boolean = false;
  @Input() isServiceLookup: boolean = false;
  @Input() confirmationTitle: string = '';
  @Input() confirmationContent: string = '';
  @Input() pricings: any = {};
  @Input() itemSettings: any = {};
  @Input() customFleetSettings: any = {};
  @Input() roNum: string = '';

  usd: string = currency.USD;
  callTimestamp: number = 0;
  motorCategories: any[] = [];
  selectedCategory: any = null;
  selectedGroup: any = null;
  applications: any[] = [];
  systemsCount: any = {};
  groupsCount: any = {};
  isMotorServiceLoading: boolean = true;
  socketListener: Subject<boolean> = new Subject<boolean>();
  dataListener: Subscription;
  vehicleAge: number = 0;
  vehicle: any = {};
  socketId: any = '';
  focus: any = {};
  motorAdditionalServiceLoading: any = {};
  motorIncludedOperationLoading: any = {};
  additionalCannedServices: any = {};
  motorIncludedServices: any = {};
  units: any = units;
  numberObj = Number;
  addServiceTransition: any = {};
  itemsPerPage: number = 15;
  pageIndex: number = 0;
  totalPages: number = 0;
  isMoreLoading: boolean = false;

  constructor(
    private calculationService: CalculationService,
    private helperService: HelperService,
    private httpService: HTTPService,
    private dataService: DataService,
    private socketService: SocketService,
    private dialogService: DialogService,
  ) {}

  ngOnInit() {}

  ngOnChanges(change: any) {
    const { motorVehicleId } = change;

    if (motorVehicleId) {
      if (!motorVehicleId.previousValue && motorVehicleId.currentValue) {
        this.initializeMotorServices();
      }
    }
  }

  ngOnDestroy() {
    if (this.socketListener) {
      this.socketListener.unsubscribe();
    }

    if (this.dataListener) {
      this.dataListener.unsubscribe();
    }
  }

  initializeMotorServices() {
    this.setSockets();
    this.setDataService();
    this.getMotorCategory();
  }

  goBackToCategory() {
    this.selectedCategory = null;
    this.selectedGroup = null;
    this.applications = [];
    this.pageIndex = 0;
    this.totalPages = 0;
    this.getMotorCategoryService();
  }

  getMotorCategoryService() {
    const { CountryID, EngineID, SubModelID } = this.motorVehicle;
    const query = `EN=${EngineID}&CO=${CountryID}&SM=${SubModelID}&search=${this.query}&itemsPerPage=${this.itemsPerPage}&pageIndex=${this.pageIndex}`;

    let systemId = this.selectedCategory?.SystemID || '';
    let groupId = this.selectedGroup?.GroupID || '';

    if (!this.query && !systemId && !groupId && !this.pageIndex) {
      this.systemsCount = {};
      this.groupsCount = {};

      if (!systemId) {
        this.applications = [];
      }

      return;
    }

    if (!this.pageIndex) {
      this.callTimestamp = Date.now();
    }

    if (systemId === 'all') {
      systemId = '';
      groupId = '';
    }

    this.isMotorServiceLoading = !this.pageIndex;
    this.isMoreLoading = !!this.pageIndex;
    this.httpService
      .getMotorCategoriesService(this.motorVehicleId, systemId, groupId, query, this.callTimestamp)
      .then(
        (res) => {
          if (res.callTimestamp !== this.callTimestamp) {
            return;
          }
          this.totalPages = Math.ceil(res.totalRecords / 15);
          this.systemsCount = {};
          this.groupsCount = {};

          if (res && res.Applications) {
            this.setMotorService(res.Applications, res.Attributes, groupId, false, '', !!this.pageIndex);
          }

          if (res.optionalServices && res.optionalServices.length) {
            res.optionalServices.forEach(({ ApplicationID, Applications, Attributes }) => {
              this.setMotorService(Applications, Attributes, null, true, ApplicationID);
              this.additionalCannedServices[ApplicationID].forEach((application: any, index: number) => {
                application.serviceTotal = {
                  labor: 0,
                  parts: 0,
                  fluids: 0,
                  total: 0,
                };
                application.count = {
                  labor: 0,
                  parts: 0,
                  fluids: 0,
                  total: 0,
                };
                application.items.forEach(({ type, subTotal, count }) => {
                  application.serviceTotal[type] += subTotal;
                  application.serviceTotal.total += subTotal;
                  application.count[type] += count;
                });
              });
            });
          }

          this.socketId = res.socketId;
        },
        (err) => {
          this.helperService.showToaster('error', err);
        },
      )
      .finally(() => {
        this.isMotorServiceLoading = false;
        this.isMoreLoading = false;
      });
  }

  getMotorCategory() {
    if (this.motorVehicle && this.motorVehicle.Year) {
      this.vehicleAge = new Date().getFullYear() - this.motorVehicle.Year;
    }

    this.httpService
      .getMotorCategories(this.motorVehicleId)
      .then(
        (res) => {
          if (res && res.Systems) {
            this.motorCategories = [{ Name: 'All', SystemID: 'all', Groups: [] }, ...sortBy(res.Systems, 'Name')];

            if (res.appsettings) {
              this.setMotorTypeSettings(res.appsettings, res.motorTag);
            }
          }
        },
        (err) => {
          this.helperService.showToaster('error', err);
        },
      )
      .finally(() => {
        this.isMotorServiceLoading = false;
      });
  }

  makeItemObject(motorItem: any, type: string, attributes: any, systemId: string, isOneSide: boolean) {
    let { DisplayName } = motorItem;
    const { Items, Position, Taxonomy, Qualifiers, ApplicationID, AttributeMappings } = motorItem;
    const attrMap = this.helperService.getMappedAttribute(attributes);
    let Item: any;

    if (Items && Items.length) {
      [Item] = Items && Items.filter(({ IsActive }) => IsActive);
    }

    if (!DisplayName && Taxonomy && Taxonomy.LiteralName) {
      DisplayName = Taxonomy.LiteralName;
    }

    let notes = '',
      count = 0,
      code = '',
      price = 0;

    let qualifiers = Qualifiers.filter(({ IsActive }) => IsActive !== false);
    let unitOfMeasuring = '';

    if (Item) {
      const { Grade, Notes, PartNumber, Quantity = 0, UnitOfMeasure, PricingFamilies } = Item;

      count = Quantity;
      code = PartNumber;
      unitOfMeasuring = UnitOfMeasure;

      if (!Quantity || Quantity === 0 || isOneSide) {
        count = 1;
      }

      if (Notes && Notes.length) {
        Notes.forEach(({ Text }) => {
          notes += Text;
        });
      }

      if (Grade && Grade.Description && !notes.includes(Grade.Description)) {
        notes = `${Grade.Description}\n${notes}`;
      }

      const [PricingFamily] = PricingFamilies;

      if (PricingFamily && PricingFamily.Pricing && PricingFamily.Pricing.length) {
        price = PricingFamily.Pricing[0].Price || 0;
      }
    }

    const item: any = {
      code,
      type,
      count,
      actualLaborTime: count,
      defaultCount: count,
      cost: this.calculationService.calculatingMotorPartCost(price, this.itemSettings.part.pricing),
      price,
      status: true,
      _id: 'no-id',
      title: DisplayName,
      description: notes.trim(),
      itemSource: 'Motor',
      subTotal: this.calculationService.roundOff(price * count),
      itemSourceId: ApplicationID,
      qboId: this.itemSettings.part.qboId,
      isTaxable: this.itemSettings.part.isTaxable,
      commissionable: this.itemSettings.part.commissionable,
      displayHoursOnOrder: this.itemSettings.part.displayHoursOnOrder,
      displayDescOnOrder: this.itemSettings.part.displayDescOnOrder,
      subheading: '',
      tags: [],
      unitOfMeasuring: '',
    };

    item.quantity = this.helperService.renderItemQuantity(item);

    if (this.itemSettings.part && this.itemSettings.part.pricing && this.itemSettings.part.pricing._id) {
      item.pricing = {
        pricingId: this.itemSettings.part.pricing._id,
        name: this.itemSettings.part.pricing.name,
      };
    }

    if (this.customFleetSettings) {
      const { isPricingMatrixOverride, customPartPricingMatrix } = this.customFleetSettings;

      if (isPricingMatrixOverride && customPartPricingMatrix) {
        item.pricing = { pricingId: customPartPricingMatrix.pricingId, name: customPartPricingMatrix.name };
        item.price = this.calculationService.calculateMarkUpUsingPricing(item.cost, customPartPricingMatrix);
      }
    }

    if (unitOfMeasuring) {
      item.unitOfMeasuring = unitOfMeasuring.toLowerCase();
    }

    if (this.selectedMotorServices[systemId]) {
      item.count = Number(this.selectedMotorServices[systemId].itemCount[item.itemSourceId]);

      item.subTotal = item.price * item.count;
    }

    if (this.itemSettings.labor.motorTag) {
      item.tags.push(this.itemSettings.part.motorTag);
    }

    if (Position && Position.Name && Position.Name !== 'N/A') {
      if (code && code !== 'NA' && code !== 'NSS') {
        item.subheading = `#${code}`;
      }

      if (Position.Name) {
        item.subheading += `${code ? ' - ' : ''}${Position.Name}`;
      }
    }

    if (qualifiers && qualifiers.length) {
      qualifiers.forEach(({ Description }) => {
        if (Description) {
          item.subheading += `${item.subheading ? ' - ' : ''}${Description}`;
        }
      });
    }

    if (AttributeMappings && AttributeMappings.length) {
      AttributeMappings.forEach((attr: any) => {
        if (attrMap[attr.Type] && attrMap[attr.Type][attr.ID]) {
          const data = attrMap[attr.Type][attr.ID];

          if (attr.Type === 'BS' && data) {
            item.subheading += `${item.subheading ? ' - ' : ''}Body Style: ${data.DoorCount} Door - ${data.Type}`;
          }

          if (attr.Type === 'BR' && data) {
            item.subheading += `${item.subheading ? ' - ' : ''}Brake: ${data.FrontType ? `${data.FrontType}/` : ''}${data.RearType ? `${data.RearType}` : ''}${data.ABS ? `/${data.ABS}` : ''}`;
          }
        }
      });
    }

    item.description = `${item.subheading} ${item.description}`;

    return item;
  }

  addToEstmateMotor(service: any, isAdded: boolean, handleSelectAll: Function, event: any, showConfirmation: boolean = true, index: number = -1) {
    event.stopPropagation();
    const { SApplicationID, items } = service;

    if (isAdded && showConfirmation && this.hasQuantityChanged(service)) {
      this.openConfirmationModal(service, isAdded, handleSelectAll, event, index);
      return;
    }

    if (SApplicationID) {
      this.addServiceTransition[SApplicationID] = true;
      setTimeout(() => {
        try {
          this.addServiceTransition[SApplicationID] = false;
        } catch (err) {}
      }, 500);

      if (!isAdded) {
        this.selectedMotorServices[SApplicationID] = {
          ...service,
          items: items.filter(({ type }) => type === 'labor'),
          extra: { isAdded: true },
        };
      } else {
        delete this.selectedMotorServices[SApplicationID];
      }
    }

    handleSelectAll(!isAdded ? 'select' : 'deselect', event, true, true);
  }

  handleFocus(itemId: string, isDisabled: boolean) {
    if (isDisabled) {
      return;
    }

    this.focus = { [itemId]: true };
  }

  handleInputClick(item: any, isDisabled: boolean) {
    if (isDisabled) {
      const unit = item.type === 'fluids' ? units.fluids[item.unitOfMeasuring] : units[item.type];

      this.helperService.showToaster('error', `Select the service to edit the ${unit}`);
      return;
    }
  }

  handleBlur(systemId: string, itemId: string, index: string, isAdditionalService: boolean = false, additionalApplicationId: string = '') {
    if (this.selectedMotorServices[systemId]) {
      if (!isAdditionalService) {
        this.applications[index].serviceTotal = {
          labor: 0,
          parts: 0,
          fluids: 0,
          total: 0,
        };
        this.applications[index].count = {
          labor: 0,
          parts: 0,
          fluids: 0,
          total: 0,
        };
        this.applications[index].items.forEach((item: any, itemIndex: number) => {
          let { type, itemSourceId, laborType, count } = item;

          if (this.selectedMotorServices[systemId].itemCount[itemSourceId]) {
            let qty = Number(this.selectedMotorServices[systemId].itemCount[itemSourceId]);

            if (Number(qty) > 1000) {
              this.selectedMotorServices[systemId].itemCount[itemSourceId] = Number(qty);
              qty = count;
              this.helperService.showToaster('error', 'Qty cannot be greater than 1000');
            }

            this.applications[index].items[itemIndex].count = qty;
            item.count = qty;
          }

          if (type === 'labor') {
            item.costEstimatedHours = item.count;
            item.billableHours = item.count;
            item.cost = item.costEstimatedHours * this.pricings[laborType].cost;
          }

          item.subTotal = item.price * item.count;
          this.applications[index].serviceTotal[type] += item.subTotal;
          this.applications[index].serviceTotal.total += item.subTotal;
          this.applications[index].count[type] += item.count;
        });
      } else {
        this.additionalCannedServices[additionalApplicationId][index].serviceTotal = {
          labor: 0,
          parts: 0,
          fluids: 0,
          total: 0,
        };
        this.additionalCannedServices[additionalApplicationId][index].count = {
          labor: 0,
          parts: 0,
          fluids: 0,
          total: 0,
        };
        this.additionalCannedServices[additionalApplicationId][index].items.forEach((item: any, itemIndex: number) => {
          let { type, itemSourceId, laborType, count } = item;

          if (this.selectedMotorServices[systemId].itemCount[itemSourceId]) {
            let qty = Number(this.selectedMotorServices[systemId].itemCount[itemSourceId]);
            if (Number(qty) > 1000) {
              this.selectedMotorServices[systemId].itemCount[itemSourceId] = Number(qty);
              qty = count;
              this.helperService.showToaster('error', 'Qty cannot be greater than 1000');
            }

            this.additionalCannedServices[additionalApplicationId][index].items[itemIndex].count = qty;
            item.count = qty;
          }

          if (type === 'labor') {
            item.costEstimatedHours = item.count;
            item.billableHours = item.count;
            item.cost = item.costEstimatedHours * this.pricings[laborType].cost;
          }

          item.subTotal = item.price * item.count;
          this.additionalCannedServices[additionalApplicationId][index].serviceTotal[type] += item.subTotal;
          this.additionalCannedServices[additionalApplicationId][index].serviceTotal.total += item.subTotal;
          this.additionalCannedServices[additionalApplicationId][index].count[type] += item.count;
        });
      }
    }

    this.focus[`${systemId}${itemId}`] = false;
  }

  handleChange(systemId: string, itemId: string, value: any) {
    if (this.selectedMotorServices[systemId] && this.selectedMotorServices[systemId].itemCount) {
      this.selectedMotorServices[systemId].itemCount[itemId] = value;
    }
  }

  handleRevertManualHour(systemId: string, itemId: string, value: any, applicationIndex: string, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    if (this.selectedMotorServices[systemId] && this.selectedMotorServices[systemId].itemCount) {
      this.selectedMotorServices[systemId].itemCount[itemId] = value;
      this.handleBlur(systemId, itemId, applicationIndex);
    }
  }

  handleSelectCategory(category: any, itemCount: number) {
    if (this.query.length > 2 && !itemCount) {
      return;
    }

    this.pageIndex = 0;
    this.totalPages = 0;
    category.Groups = sortBy(category.Groups, 'Name');

    this.selectedCategory = category;
    this.getMotorCategoryService();
  }

  handleSelectGroup(group: any, itemCount: number) {
    if (this.query.length > 2 && !itemCount) {
      return;
    }
    this.pageIndex = 0;
    this.totalPages = 0;
    this.selectedGroup = group;
    this.getMotorCategoryService();
  }

  hasQuantityChanged = (service: any) => {
    let hasChanged = false;

    const { itemCount, defaultItemCount } = service;
    const keys = Object.keys(itemCount);

    for (let i = 0; i < keys.length; i++) {
      if (Number(itemCount[keys[i]]) !== Number(defaultItemCount[keys[i]])) {
        hasChanged = true;
        break;
      }
    }

    return hasChanged;
  };

  handleReset({ service, index }) {
    const { defaultItemCount, items } = service;

    this.applications[index].itemCount = this.helperService.deepCopy(defaultItemCount);
    this.applications[index].serviceTotal = {
      labor: 0,
      parts: 0,
      fluids: 0,
      total: 0,
    };
    this.applications[index].count = {
      labor: 0,
      parts: 0,
      fluids: 0,
      total: 0,
    };

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const count = defaultItemCount[item.itemSourceId];
      this.applications[index].items[i].count = count;

      if (item.type === 'labor') {
        this.applications[index].items[i].costEstimatedHours = count;
        this.applications[index].items[i].billableHours = count;
        this.applications[index].items[i].cost = count * this.pricings[item.laborType].cost;
      }

      this.applications[index].items[i].subTotal = item.price * count;
      this.applications[index].serviceTotal[item.type] += item.subTotal;
      this.applications[index].serviceTotal.total += item.subTotal;
      this.applications[index].count[item.type] += count;
    }
  }

  handleSelectChangeMotor(event: any, application: any) {
    const { list } = event;
    const { SApplicationID } = application;

    if (list.length) {
      this.selectedMotorServices[SApplicationID] = {
        ...application,
        ...this.selectedMotorServices[SApplicationID],
        extra: { isAdded: true },
        items: this.helperService.deepCopy(list),
      };
    } else {
      delete this.selectedMotorServices[SApplicationID];
    }
  }

  handleExpandMotorService(isExpanded: boolean, application: any) {
    if (isExpanded) {
      const { CountryID, EngineID, SubModelID } = this.motorVehicle;
      const applicationId = application.ApplicationID;
      const query = {
        EN: EngineID,
        CO: CountryID,
        SM: SubModelID,
        search: this.query,
        motorVehicleId: this.motorVehicleId,
        applicationId: applicationId,
        systemId: this.selectedCategory?.SystemID,
        groupId: this.selectedGroup && this.selectedGroup.GroupID,
      };

      if (this.selectedCategory?.SystemID === 'all') {
        delete query.systemId;
        delete query.groupId;
      }

      this.callTimestamp = Date.now();

      if (!this.motorIncludedOperationLoading[applicationId]) {
        this.motorIncludedOperationLoading[applicationId] = true;
        this.httpService
          .getMotorIncludedOperations(query, this.callTimestamp)
          .then(
            (res) => {
              if (res && res[applicationId]) {
                this.motorIncludedServices[applicationId] = res[applicationId].includedOperations || [];
              }
            },
            (err) => {
              this.helperService.showToaster('error', err);
            },
          )
          .finally(() => {
            this.motorIncludedOperationLoading[applicationId] = false;
          });
      }

      if (!this.motorAdditionalServiceLoading[applicationId]) {
        this.motorAdditionalServiceLoading[applicationId] = true;
        this.httpService.getAdditionalMotorService(query, this.callTimestamp).then(
          (services) => {
            if (services && services.Applications && services.Applications.length) {
              if (!this.additionalCannedServices[application.SApplicationID]) {
                this.setMotorService(services.Applications, services.Attributes, null, true, application.SApplicationID);

                this.additionalCannedServices[application.SApplicationID].forEach((application: any, index: number) => {
                  application.serviceTotal = {
                    labor: 0,
                    parts: 0,
                    fluids: 0,
                    total: 0,
                  };
                  application.count = {
                    labor: 0,
                    parts: 0,
                    fluids: 0,
                    total: 0,
                  };
                  application.items.forEach(({ type, subTotal, count }) => {
                    application.serviceTotal[type] += subTotal;
                    application.serviceTotal.total += subTotal;
                    application.count[type] += count;
                  });
                });
              }
            }
            this.motorAdditionalServiceLoading[applicationId] = false;
          },
          (err) => {
            this.motorAdditionalServiceLoading[applicationId] = false;
            this.helperService.showToaster('error', err);
          },
        );
      }
    }
  }

  openConfirmationModal(service: any, isAdded: boolean, handleSelectAll: Function, event: any, index: number) {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '35%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        title: this.confirmationTitle,
        message: this.confirmationContent,
        button: {
          success: { title: 'Confirm' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        this.addToEstmateMotor(service, isAdded, handleSelectAll, event, false);
        this.handleReset({ service, index });
      }

      onClose.unsubscribe();
    });
  }

  calculateLaborMarkupTime(motorLaborTime: number) {}

  setMotorService(motorService: any[], attributes: any, groupId: string = '', isAdditionalServices: boolean = false, SApplicationID: string = '', isShowMore: boolean = false) {
    if (!isAdditionalServices && !isShowMore) {
      this.applications = [];
    }

    motorService.forEach((application: any) => {
      const { Items, Position, Taxonomy, Links, Qualifiers, ApplicationID, AttributeMappings } = application;
      let DisplayName = application.DisplayName || '';
      application.subheading = '';
      application.SApplicationID = ApplicationID;

      // Setting repair diagram links
      if (Links && Links.length) {
        const [vectorLink] = Links.filter(({ Rel }) => Rel === 'PartVectorIllustrationsSummary');

        if (vectorLink && vectorLink.Rel) {
          application.hasRepairDiagram = true;
          application.repairDiagramLink = vectorLink.Href;
        }
      }

      if (Taxonomy) {
        if (!this.systemsCount.all) {
          this.systemsCount.all = 0;
        }

        if (this.systemsCount[Taxonomy.SystemID]) {
          this.systemsCount[Taxonomy.SystemID] += 1;
          this.systemsCount.all += 1;
        } else {
          this.systemsCount[Taxonomy.SystemID] = 1;
          this.systemsCount.all += 1;
        }

        if (this.groupsCount[Taxonomy.GroupID]) {
          this.groupsCount[Taxonomy.GroupID] += 1;
        } else {
          this.groupsCount[Taxonomy.GroupID] = 1;
        }

        if (!DisplayName) {
          DisplayName = Taxonomy.LiteralName || '';
        }
      }

      const [Item] = Items;
      const skillType = ['General', 'Precision', 'Maintenance'];
      let time = 0,
        notes = '',
        laborType = '',
        timedescription = '',
        allTimeLabor: '',
        allTimeLaborDescription: '';
      let qualifiers = Qualifiers.filter(({ IsActive }) => IsActive !== false);

      if (Item) {
        const { Notes, BaseLaborTime, RequiredSkill, AllLaborTime, AllLaborTimeDescription, LaborTimeInterval, BaseLaborTimeDescription } = Item;

        allTimeLabor = AllLaborTime;
        allTimeLaborDescription = AllLaborTimeDescription;
        timedescription = BaseLaborTimeDescription;

        if (allTimeLabor) {
          application.isOneSide = true;
        } else {
          application.isOneSide = false;
        }

        if (new RegExp(/Hours/, 'i').test(LaborTimeInterval)) {
          time += Number(BaseLaborTime);
        }

        if (RequiredSkill) {
          laborType = RequiredSkill.Name;
        }

        if (Notes && Notes.length) {
          Notes.forEach(({ Text }) => {
            notes += Text;
          });
        }
      }

      const laborItem: any = {
        cost: 0,
        price: 0,
        laborType,
        subTotal: 0,
        billRate: 0,
        actualLaborTime: time,
        motorMarkup: 0,
        count: time,
        _id: 'no-id',
        status: true,
        type: 'labor',
        title: DisplayName,
        description: notes,
        billableHours: time,
        itemSource: 'Motor',
        costEstimatedHours: time,
        itemSourceId: ApplicationID,
        qboId: this.itemSettings.labor.qboId,
        isTaxable: this.itemSettings.labor.isTaxable,
        commissionable: this.itemSettings.labor.commissionable,
        displayHoursOnOrder: this.itemSettings.labor.displayHoursOnOrder,
        displayDescOnOrder: this.itemSettings.labor.displayDescOnOrder,
        tags: [],
        subheading: '',
      };
      application.notes = notes;

      if (this.itemSettings?.motorLaborTimeMarkup?._id) {
        const { value, markup } = this.calculationService.calculateLaborMarkupTime(this.vehicleAge, laborItem.actualLaborTime, this.itemSettings.motorLaborTimeMarkup);

        laborItem.count = value;
        laborItem.motorMarkup = markup;
      }

      if (this.selectedMotorServices[application.SApplicationID]) {
        laborItem.count = Number(this.selectedMotorServices[application.SApplicationID].itemCount[laborItem.itemSourceId]);
        laborItem.billableHours = laborItem.count;
        laborItem.costEstimatedHours = laborItem.count;
      }

      application.itemCount = {
        [laborItem.itemSourceId]: laborItem.count,
      };
      application.defaultItemCount = {
        [laborItem.itemSourceId]: laborItem.count,
      };

      if (this.itemSettings.labor.motorTag) {
        laborItem.tags.push(this.itemSettings.labor.motorTag);
      }

      if (skillType.indexOf(laborType) !== -1) {
        if (this.pricings[laborType]) {
          const { isLaborRateOverride, customLaborRate } = this.customFleetSettings;
          let billRate = this.pricings[laborType].price;
          let pricing = this.pricings[laborType].pricing;

          if (isLaborRateOverride && customLaborRate) {
            pricing = customLaborRate;
            billRate = customLaborRate && customLaborRate.rate;
          }

          laborItem.cost = this.pricings[laborType].cost * laborItem.costEstimatedHours;
          laborItem.billRate = billRate;
          laborItem.price = laborItem.billRate;
          laborItem.subTotal = laborItem.billRate * laborItem.count;
          laborItem.pricing = pricing;
        }
      } else {
        if (this.pricings.General) {
          const { isLaborRateOverride, customLaborRate } = this.customFleetSettings;
          let billRate = this.pricings.General.price;
          let pricing = this.pricings.General.pricing;

          if (isLaborRateOverride && customLaborRate) {
            pricing = customLaborRate;
            billRate = customLaborRate && customLaborRate.rate;
          }

          laborItem.cost = this.pricings.General.cost * laborItem.costEstimatedHours;
          laborItem.billRate = billRate;
          laborItem.price = laborItem.billRate;
          laborItem.subTotal = laborItem.billRate * laborItem.count;
          laborItem.pricing = pricing;
        }
      }

      application.serviceTotal = {
        labor: laborItem.subTotal,
        parts: 0,
        fluids: 0,
        total: laborItem.subTotal,
      };

      application.count = {
        labor: laborItem.count,
        parts: 0,
        fluids: 0,
      };

      application.DisplayName = this.helperService.makeMotorServiceName(DisplayName, Position, timedescription, qualifiers, AttributeMappings, attributes, 'service');
      laborItem.title = this.helperService.makeMotorServiceName(DisplayName, Position, timedescription, qualifiers, AttributeMappings, attributes, 'labor');
      application.subheading = this.helperService.makeMotorServiceName(DisplayName, Position, timedescription, qualifiers, AttributeMappings, attributes, 'heading');
      application.items = [laborItem];

      if (this.selectedCategory || this.query?.length > 3) {
        if (isAdditionalServices) {
          if (!this.additionalCannedServices[SApplicationID]) {
            this.additionalCannedServices[SApplicationID] = [];
          }

          application.isAdditionalService = true;
          this.additionalCannedServices[SApplicationID].push(application);
          this.setPartsFluidsForAdditionalService(application.parts, application.fluids, SApplicationID, application.SApplicationID, application.isOneSide);
        } else {
          this.applications.push(application);
        }
      }

      if (allTimeLabor && application.items[0]) {
        const duplicateService = this.helperService.deepCopy(application);
        duplicateService.isOneSide = false;
        duplicateService.SApplicationID = `${application.SApplicationID}_D`;

        if (allTimeLaborDescription) {
          duplicateService.DisplayName = this.helperService.makeMotorServiceName(DisplayName, Position, allTimeLaborDescription, qualifiers, AttributeMappings, attributes, 'service');
          duplicateService.items[0].title = this.helperService.makeMotorServiceName(DisplayName, Position, allTimeLaborDescription, qualifiers, AttributeMappings, attributes, 'labor');
        }

        duplicateService.items[0].actualLaborTime = allTimeLabor;
        duplicateService.items[0].costEstimatedHours = allTimeLabor;

        if (this.itemSettings?.motorLaborTimeMarkup?._id) {
          const { value, markup } = this.calculationService.calculateLaborMarkupTime(this.vehicleAge, duplicateService.items[0].actualLaborTime, this.itemSettings.motorLaborTimeMarkup);

          duplicateService.items[0].count = value;
          duplicateService.items[0].motorMarkup = markup;
        }

        if (this.selectedMotorServices[duplicateService.SApplicationID]) {
          duplicateService.items[0].count = Number(this.selectedMotorServices[duplicateService.SApplicationID].itemCount[duplicateService.items[0].itemSourceId]);
          duplicateService.items[0].billableHours = duplicateService.items[0].count;
          duplicateService.items[0].costEstimatedHours = duplicateService.items[0].count;
        }

        if (skillType.indexOf(laborType) !== -1) {
          if (duplicateService.items[0]) {
            const { isLaborRateOverride, customLaborRate } = this.customFleetSettings;
            let billRate = this.pricings[laborType].price;
            let pricing = this.pricings[laborType].pricing;

            if (isLaborRateOverride && customLaborRate) {
              pricing = customLaborRate;
              billRate = customLaborRate && customLaborRate.rate;
            }

            duplicateService.items[0].cost = this.pricings[laborType].cost * duplicateService.items[0].costEstimatedHours;
            duplicateService.items[0].billRate = billRate;
            duplicateService.items[0].price = duplicateService.items[0].billRate;
            duplicateService.items[0].subTotal = duplicateService.items[0].billRate * duplicateService.items[0].count;
            duplicateService.items[0].pricing = pricing;
          }
        } else {
          const { isLaborRateOverride, customLaborRate } = this.customFleetSettings;
          let billRate = this.pricings.General.price;
          let pricing = this.pricings.General.pricing;

          if (isLaborRateOverride && customLaborRate) {
            pricing = customLaborRate;
            billRate = customLaborRate && customLaborRate.rate;
          }
          if (duplicateService.items[0]) {
            duplicateService.items[0].cost = this.pricings.General.cost * duplicateService.items[0].costEstimatedHours;
            duplicateService.items[0].billRate = billRate;
            duplicateService.items[0].price = duplicateService.items[0].billRate;
            duplicateService.items[0].subTotal = duplicateService.items[0].billRate * duplicateService.items[0].count;
            duplicateService.items[0].pricing = pricing;
          }
        }

        duplicateService.itemCount = {
          [duplicateService.items[0].itemSourceId]: duplicateService.items[0].count,
        };
        duplicateService.defaultItemCount = {
          [duplicateService.items[0].itemSourceId]: duplicateService.items[0].count,
        };

        duplicateService.serviceTotal = {
          labor: duplicateService.items[0].subTotal,
          parts: 0,
          fluids: 0,
          total: duplicateService.items[0].subTotal,
        };

        duplicateService.count = {
          labor: duplicateService.items[0].count,
          parts: 0,
          fluids: 0,
        };

        if (this.selectedCategory || this.query?.length > 3) {
          if (isAdditionalServices) {
            if (!this.additionalCannedServices[SApplicationID]) {
              this.additionalCannedServices[SApplicationID] = [];
            }

            duplicateService.isAdditionalService = true;
            this.additionalCannedServices[SApplicationID].push(duplicateService);
            this.setPartsFluidsForAdditionalService(duplicateService.parts, duplicateService.fluids, SApplicationID, duplicateService.SApplicationID, duplicateService.isOneSide);
          } else {
            this.applications.push(duplicateService);
          }
        }

        if (Taxonomy) {
          this.systemsCount[Taxonomy.SystemID] += 1;
          this.systemsCount.all += 1;

          if (!groupId) {
            this.groupsCount[Taxonomy.GroupID] += 1;
          }
        }
      }
    });
  }

  setSockets() {
    const { userId } = this.helperService.getCompany();

    if (this.socketListener) {
      this.socketListener.unsubscribe();
    }
    this.socketListener = new Subject<boolean>();
    this.socketService
      .socketOn(`motor-service-item${this.motorVehicleId}${userId}`)
      .pipe(takeUntil(this.socketListener))
      .subscribe((motorServiceData) => {
        if (motorServiceData) {
          const { data, socketId, systemID, applicationId, vehicleBaseId } = motorServiceData;
          const { parts, fluids } = data;
          const systemId = this.selectedCategory?.SystemID;

          if (socketId === this.socketId && `${vehicleBaseId}` === `${this.motorVehicleId}`) {
            if (`${systemId || ''}` === `${systemID}` || systemId === 'all') {
              for (let i = 0; i < this.applications.length; i++) {
                const application = this.applications[i];
                const { SApplicationID, isOneSide } = application;

                if (`${application.ApplicationID}` === `${applicationId}`) {
                  parts.Applications.forEach((application: any) => {
                    const item = this.makeItemObject(application, 'parts', parts.Attributes, SApplicationID, isOneSide);
                    this.applications[i].itemCount[item.itemSourceId] = item.count;
                    this.applications[i].defaultItemCount[item.itemSourceId] = item.defaultCount;
                    this.applications[i].items.push(item);
                  });

                  fluids.Applications.forEach((application: any) => {
                    const item = this.makeItemObject(application, 'fluids', parts.Attributes, SApplicationID, isOneSide);
                    this.applications[i].itemCount[item.itemSourceId] = item.count;
                    this.applications[i].defaultItemCount[item.itemSourceId] = item.defaultCount;
                    this.applications[i].items.push(item);
                  });

                  this.applications[i].serviceTotal = {
                    labor: 0,
                    parts: 0,
                    fluids: 0,
                    total: 0,
                  };
                  this.applications[i].count = {
                    labor: 0,
                    parts: 0,
                    fluids: 0,
                    total: 0,
                  };
                  this.applications[i].items.forEach(({ type, subTotal, count }) => {
                    this.applications[i].serviceTotal[type] += subTotal;
                    this.applications[i].serviceTotal.total += subTotal;
                    this.applications[i].count[type] += count;
                  });
                }
              }
            }
          }
        }
      });
  }

  setDataService() {
    this.dataListener = this.dataService.getData().subscribe(({ data }) => {
      if (data.key === 'motor-on-search') {
        this.query = data.data.query;
        this.pageIndex = 0;
        this.totalPages = 0;
        this.getMotorCategoryService();
      }
    });
  }

  setPartsFluidsForAdditionalService(parts: any, fluids: any, sApplicationId: string, applicationId: string, isOneSide: boolean) {
    let aIndex = -1;
    const additionalService = this.additionalCannedServices[sApplicationId];

    for (let i = 0; i < additionalService.length; i++) {
      if (additionalService[i].ApplicationID === applicationId) {
        aIndex = i;
      }
    }

    if (parts && parts.Applications && parts.Applications.length) {
      parts.Applications.forEach((application: any) => {
        const item = this.makeItemObject(application, 'parts', parts.Attributes, applicationId, isOneSide);

        if (aIndex !== -1 && this.additionalCannedServices[sApplicationId][aIndex]) {
          this.additionalCannedServices[sApplicationId][aIndex].itemCount[item.itemSourceId] = item.count;
          this.additionalCannedServices[sApplicationId][aIndex].defaultItemCount[item.itemSourceId] = item.defaultCount;
          this.additionalCannedServices[sApplicationId][aIndex].items.push(item);
        }
      });
    }

    if (fluids && fluids.Applications && fluids.Applications.length) {
      fluids.Applications.forEach((application: any) => {
        const item = this.makeItemObject(application, 'fluids', parts.Attributes, applicationId, isOneSide);

        if (aIndex !== -1 && this.additionalCannedServices[sApplicationId][aIndex]) {
          this.additionalCannedServices[sApplicationId][aIndex].itemCount[item.itemSourceId] = item.count;
          this.additionalCannedServices[sApplicationId][aIndex].defaultItemCount[item.itemSourceId] = item.defaultCount;
          this.additionalCannedServices[sApplicationId][aIndex].items.push(item);
        }
      });
    }
  }

  setTooltip(itemCount: number, name: string) {
    if (this.query.length > 2 && !itemCount) {
      return `${name} has 0 matching results`;
    }

    return '';
  }

  setMotorTypeSettings(appsettings: any, motorTag: any) {
    const {
      pricingId,
      partPriceId,
      isTaxablePart,
      isTaxableLabor,
      isCommissionablePart,
      isCommissionableLabor,
      mBaseLaborCost,
      partQboItemRefId,
      laborQboItemRefId,
      percisionLaborCost,
      maintainenacePriceId,
      motorLaborTimeMarkup,
      percisionLaborPriceId,
      maintainenaceLaborCost,
      displayDescOnOrderPart,
      displayHoursOnOrderPart,
      displayDescOnOrderLabor,
      displayHoursOnOrderLabor,
    } = appsettings;

    if (pricingId) {
      this.pricings.General = {
        price: pricingId.rate,
        cost: mBaseLaborCost,
        pricing: {
          pricingId: pricingId._id,
          name: pricingId.name,
        },
      };
    }

    if (percisionLaborPriceId) {
      this.pricings.Precision = {
        price: percisionLaborPriceId.rate,
        cost: percisionLaborCost,
        pricing: {
          pricingId: percisionLaborPriceId._id,
          name: percisionLaborPriceId.name,
        },
      };
    }

    if (maintainenacePriceId) {
      this.pricings.Maintenance = {
        price: maintainenacePriceId.rate,
        cost: maintainenaceLaborCost,
        pricing: {
          pricingId: maintainenacePriceId._id,
          name: maintainenacePriceId.name,
        },
      };
    }
    this.itemSettings.part = {
      motorTag: motorTag,
      isTaxable: isTaxablePart,
      commissionable: isCommissionablePart,
      displayHoursOnOrder: displayHoursOnOrderPart,
      displayDescOnOrder: displayDescOnOrderPart,
      qboId: (partQboItemRefId && partQboItemRefId.qboId) || '',
      pricing: partPriceId,
    };
    this.itemSettings.labor = {
      motorTag: motorTag,
      isTaxable: isTaxableLabor,
      commissionable: isCommissionableLabor,
      displayHoursOnOrder: displayHoursOnOrderLabor,
      displayDescOnOrder: displayDescOnOrderLabor,
      qboId: (laborQboItemRefId && laborQboItemRefId.qboId) || '',
    };
    this.itemSettings.motorLaborTimeMarkup = motorLaborTimeMarkup;
  }

  setCount(systemId: string, itemSource: string, count: number) {
    if (this.selectedMotorServices[systemId] && this.selectedMotorServices[systemId].itemCount && this.selectedMotorServices[systemId].itemCount[itemSource]) {
      return this.selectedMotorServices[systemId].itemCount[itemSource];
    }

    return count;
  }

  openRepairDiagram(application: any, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    const { ApplicationID, DisplayName } = application;
    const { CountryID, EngineID, SubModelID } = this.motorVehicle;

    this.dialogService.open(RepairDiagramDialogComponent, {
      showHeader: false,
      styleClass: 'repair-diagram-dialog-ref',
      data: {
        displayName: DisplayName,
        options: {
          EN: EngineID,
          CO: CountryID,
          SM: SubModelID,
        },
        applicationId: ApplicationID,
        vehicleId: this.motorVehicleId,
        roNum: this.roNum,
      },
    });
  }

  showTooltip(actualLaborTime: number, markup: number, markUpQty) {
    return `Revert back to the Marked up value: ${Number(actualLaborTime)} * (1 + ${Number(markup)}/100) = ${Number(markUpQty)}`;
  }

  getMoreServices() {
    if (this.pageIndex < this.totalPages - 1) {
      this.pageIndex++;
      this.getMotorCategoryService();
    }
  }
}
